import type {
  AnnotationCheckpointV1,
  AnnotationSessionCborV1,
  AnnotationSnapshotV1,
  CheckpointReason,
  LabelRasterManifestEntry,
  MaskNiftiEntry
} from "@shared/lib/history-types"
import { encodeSessionCbor } from "@shared/lib/session-cbor"
import { cloneCoords, equalCoords } from "@virdx/wsi-viewer"
import { create } from "zustand"

interface HistoryStoreState {
  session: AnnotationSessionCborV1 | null
  isDirty: boolean
  isUploading: boolean
  lastUploadAt: string | null
  lastUploadError: string | null
  initSession: (params: { submissionId: string; caseId: string; initialSnapshot: AnnotationSnapshotV1 }) => void
  loadSession: (session: AnnotationSessionCborV1) => void
  captureCheckpoint: (snapshot: AnnotationSnapshotV1, reason: CheckpointReason) => void
  undo: () => AnnotationSnapshotV1 | null
  redo: () => AnnotationSnapshotV1 | null
  seekToCursor: (index: number) => AnnotationSnapshotV1 | null
  markUploadStart: () => void
  markUploadSuccess: () => void
  markUploadError: (message: string) => void
  setMaskNiftis: (entries: MaskNiftiEntry[]) => void
  setLabelRasterManifests: (entries: LabelRasterManifestEntry[]) => void
  toCbor: () => Uint8Array | null
  checkpointCount: () => number
  reset: () => void
}

function cloneSnapshot(snapshot: AnnotationSnapshotV1): AnnotationSnapshotV1 {
  return {
    masks: snapshot.masks.map((m) => ({
      ...m,
      rle: new Uint8Array(m.rle)
    })),
    vectors: snapshot.vectors.map((v) => ({
      ...v,
      annotation: {
        ...v.annotation,
        points: cloneCoords(v.annotation.points),
        holes: v.annotation.holes?.map((hole) => cloneCoords(hole)),
        style: { ...v.annotation.style },
        bbox: { ...v.annotation.bbox }
      }
    })),
    scores: snapshot.scores.map((s) => ({ ...s, values: { ...s.values } })),
    imageLabels: snapshot.imageLabels.map((l) => ({
      ...l,
      labels: { ...l.labels },
      seriesMapping: l.seriesMapping
        ? Object.fromEntries(Object.entries(l.seriesMapping).map(([k, v]) => [k, [...v]]))
        : undefined
    })),
    texts: snapshot.texts.map((t) => ({ ...t })),
    moduleCompletions: snapshot.moduleCompletions.map((c) => ({ ...c })),
    // Run byte arrays are immutable once in the store, so checkpoints share them
    // (no per-checkpoint copy in memory; the CBOR encoder dedupes them on disk).
    labelRasters: (snapshot.labelRasters ?? []).map((r) => ({
      ...r,
      source: { ...r.source, shape: [...r.source.shape], chunkShape: [...r.source.chunkShape] },
      chunks: r.chunks.map((chunk) => ({
        ...chunk,
        index: [chunk.index[0], chunk.index[1]],
        bbox: [chunk.bbox[0], chunk.bbox[1], chunk.bbox[2], chunk.bbox[3]],
        runs: chunk.runs
      })),
      classesUsed: [...r.classesUsed]
    }))
  }
}

const MAX_CHECKPOINTS = 50

/**
 * If checkpoints exceed MAX_CHECKPOINTS, trim oldest entries and adjust cursor.
 */
function trimCheckpoints(
  checkpoints: AnnotationCheckpointV1[],
  cursor: number
): { checkpoints: AnnotationCheckpointV1[]; cursor: number; trimmed: boolean } {
  if (checkpoints.length <= MAX_CHECKPOINTS) {
    return { checkpoints, cursor, trimmed: false }
  }
  const excess = checkpoints.length - MAX_CHECKPOINTS
  return {
    checkpoints: checkpoints.slice(excess),
    cursor: Math.max(0, cursor - excess),
    trimmed: true
  }
}

function cloneSession(session: AnnotationSessionCborV1): AnnotationSessionCborV1 {
  return {
    ...session,
    checkpoints: session.checkpoints.map((cp) => ({
      ...cp,
      snapshot: cloneSnapshot(cp.snapshot)
    })),
    maskNiftis: session.maskNiftis?.map((e) => ({
      annotationId: e.annotationId,
      nifti: new Uint8Array(e.nifti)
    })),
    labelRasterManifests: session.labelRasterManifests?.map((e) => ({
      annotationId: e.annotationId,
      manifest: new Uint8Array(e.manifest)
    }))
  }
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false
  }
  return true
}

function equalFlatRecord(
  a: Record<string, string | number | boolean | null>,
  b: Record<string, string | number | boolean | null>
): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false
  }
  return true
}

function equalBoolRecord(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false
  }
  return true
}

function snapshotsEqual(a: AnnotationSnapshotV1, b: AnnotationSnapshotV1): boolean {
  if (a.masks.length !== b.masks.length) return false
  for (let i = 0; i < a.masks.length; i += 1) {
    const left = a.masks[i]
    const right = b.masks[i]
    if (!(left && right)) return false
    if (
      left.id !== right.id ||
      left.seriesId !== right.seriesId ||
      left.projectModuleId !== right.projectModuleId ||
      left.labelName !== right.labelName ||
      left.labelValue !== right.labelValue ||
      left.instanceOrdinal !== right.instanceOrdinal ||
      !equalBytes(left.rle, right.rle)
    ) {
      return false
    }
  }

  if (a.vectors.length !== b.vectors.length) return false
  for (let i = 0; i < a.vectors.length; i += 1) {
    const left = a.vectors[i]
    const right = b.vectors[i]
    if (!(left && right)) return false
    if (
      left.id !== right.id ||
      left.seriesId !== right.seriesId ||
      left.projectModuleId !== right.projectModuleId ||
      left.labelName !== right.labelName ||
      left.labelValue !== right.labelValue
    ) {
      return false
    }

    if (
      left.annotation.id !== right.annotation.id ||
      left.annotation.imageName !== right.annotation.imageName ||
      left.annotation.label !== right.annotation.label ||
      left.annotation.createdAt !== right.annotation.createdAt ||
      left.annotation.updatedAt !== right.annotation.updatedAt ||
      left.annotation.style.strokeColor !== right.annotation.style.strokeColor ||
      left.annotation.style.strokeOpacity !== right.annotation.style.strokeOpacity ||
      left.annotation.style.fillColor !== right.annotation.style.fillColor ||
      left.annotation.style.fillOpacity !== right.annotation.style.fillOpacity ||
      left.annotation.style.strokeWidth !== right.annotation.style.strokeWidth ||
      left.annotation.bbox.left !== right.annotation.bbox.left ||
      left.annotation.bbox.top !== right.annotation.bbox.top ||
      left.annotation.bbox.right !== right.annotation.bbox.right ||
      left.annotation.bbox.bottom !== right.annotation.bbox.bottom ||
      !equalCoords(left.annotation.points, right.annotation.points)
    ) {
      return false
    }

    const leftHoles = left.annotation.holes ?? []
    const rightHoles = right.annotation.holes ?? []
    if (leftHoles.length !== rightHoles.length) return false
    for (let j = 0; j < leftHoles.length; j += 1) {
      if (!equalCoords(leftHoles[j]!, rightHoles[j]!)) return false
    }
  }

  if (a.scores.length !== b.scores.length) return false
  for (let i = 0; i < a.scores.length; i += 1) {
    const left = a.scores[i]
    const right = b.scores[i]
    if (!(left && right)) return false
    if (
      left.id !== right.id ||
      left.seriesId !== right.seriesId ||
      left.projectModuleId !== right.projectModuleId ||
      left.labelName !== right.labelName ||
      left.labelValue !== right.labelValue ||
      !equalFlatRecord(left.values, right.values)
    ) {
      return false
    }
  }

  if (a.imageLabels.length !== b.imageLabels.length) return false
  for (let i = 0; i < a.imageLabels.length; i += 1) {
    const left = a.imageLabels[i]
    const right = b.imageLabels[i]
    if (!(left && right)) return false
    if (
      left.id !== right.id ||
      left.seriesId !== right.seriesId ||
      left.projectModuleId !== right.projectModuleId ||
      !equalBoolRecord(left.labels, right.labels)
    ) {
      return false
    }
  }

  if (a.texts.length !== b.texts.length) return false
  for (let i = 0; i < a.texts.length; i += 1) {
    const left = a.texts[i]
    const right = b.texts[i]
    if (!(left && right)) return false
    if (
      left.id !== right.id ||
      left.seriesId !== right.seriesId ||
      left.projectModuleId !== right.projectModuleId ||
      left.valueText !== right.valueText
    ) {
      return false
    }
  }

  if (a.moduleCompletions.length !== b.moduleCompletions.length) return false
  for (let i = 0; i < a.moduleCompletions.length; i += 1) {
    const left = a.moduleCompletions[i]
    const right = b.moduleCompletions[i]
    if (!(left && right)) return false
    if (
      left.projectModuleId !== right.projectModuleId ||
      left.completed !== right.completed ||
      left.completedAt !== right.completedAt
    ) {
      return false
    }
  }

  const aRasters = a.labelRasters ?? []
  const bRasters = b.labelRasters ?? []
  if (aRasters.length !== bRasters.length) return false
  for (let i = 0; i < aRasters.length; i += 1) {
    const left = aRasters[i]
    const right = bRasters[i]
    if (!(left && right)) return false
    if (
      left.id !== right.id ||
      left.seriesId !== right.seriesId ||
      left.projectModuleId !== right.projectModuleId ||
      left.labelName !== right.labelName ||
      left.chunks.length !== right.chunks.length
    ) {
      return false
    }
    for (let j = 0; j < left.chunks.length; j += 1) {
      const lc = left.chunks[j]
      const rc = right.chunks[j]
      if (!(lc && rc)) return false
      if (
        lc.index[0] !== rc.index[0] ||
        lc.index[1] !== rc.index[1] ||
        lc.originalCrc32 !== rc.originalCrc32 ||
        (lc.runs !== rc.runs && !equalBytes(lc.runs, rc.runs))
      ) {
        return false
      }
    }
  }

  return true
}

function makeCheckpoint(snapshot: AnnotationSnapshotV1, reason: CheckpointReason): AnnotationCheckpointV1 {
  return {
    timestamp: Date.now(),
    reason,
    snapshot: cloneSnapshot(snapshot)
  }
}

export const useHistoryStore = create<HistoryStoreState>((set, get) => ({
  session: null,
  isDirty: false,
  isUploading: false,
  lastUploadAt: null,
  lastUploadError: null,
  initSession: ({ submissionId, caseId, initialSnapshot }) => {
    const now = new Date().toISOString()
    set({
      session: {
        version: 3,
        submissionId,
        caseId,
        updatedAt: now,
        cursor: 0,
        checkpoints: [makeCheckpoint(initialSnapshot, "save")]
      },
      isDirty: false,
      isUploading: false,
      lastUploadAt: null,
      lastUploadError: null
    })
  },
  loadSession: (session) => {
    const cloned = cloneSession(session)
    const { checkpoints, cursor, trimmed } = trimCheckpoints(cloned.checkpoints, cloned.cursor)
    set({
      session: { ...cloned, checkpoints, cursor },
      isDirty: trimmed,
      isUploading: false,
      lastUploadAt: null,
      lastUploadError: null
    })
  },
  captureCheckpoint: (snapshot, reason) => {
    set((state) => {
      if (!state.session) return state

      // For "save"/"submit" (cold path, ~every 15s), deduplicate via deep comparison.
      // For "mutation" (hot path, every edit), skip the expensive comparison —
      // the bridge already guarantees mutationSeq changed.
      if (reason !== "mutation") {
        const current = state.session.checkpoints[state.session.cursor]
        if (current && current.reason === reason && snapshotsEqual(current.snapshot, snapshot)) {
          return state
        }
      }

      const head = state.session.checkpoints.slice(0, state.session.cursor + 1)
      const appended = [
        ...head,
        {
          timestamp: Date.now(),
          reason,
          snapshot
        }
      ]
      const { checkpoints: nextCheckpoints, cursor: nextCursor } = trimCheckpoints(appended, appended.length - 1)
      return {
        session: {
          ...state.session,
          cursor: nextCursor,
          updatedAt: new Date().toISOString(),
          checkpoints: nextCheckpoints
        },
        isDirty: true
      }
    })
  },
  undo: () => {
    const state = get()
    if (!state.session || state.session.cursor === 0) return null

    const nextCursor = state.session.cursor - 1
    const checkpoint = state.session.checkpoints[nextCursor]
    if (!checkpoint) return null
    set({
      session: {
        ...state.session,
        cursor: nextCursor
      }
    })
    return checkpoint.snapshot
  },
  redo: () => {
    const state = get()
    if (!state.session || state.session.cursor >= state.session.checkpoints.length - 1) {
      return null
    }

    const nextCursor = state.session.cursor + 1
    const checkpoint = state.session.checkpoints[nextCursor]
    if (!checkpoint) return null
    set({
      session: {
        ...state.session,
        cursor: nextCursor
      }
    })
    return checkpoint.snapshot
  },
  seekToCursor: (index) => {
    const state = get()
    if (!state.session || state.session.checkpoints.length === 0) {
      return null
    }

    const max = state.session.checkpoints.length - 1
    const clamped = Math.max(0, Math.min(max, Math.trunc(index)))
    const checkpoint = state.session.checkpoints[clamped]
    if (!checkpoint) return null

    if (state.session.cursor !== clamped) {
      set({
        session: {
          ...state.session,
          cursor: clamped
        }
      })
    }

    return checkpoint.snapshot
  },
  markUploadStart: () => set({ isUploading: true, lastUploadError: null }),
  markUploadSuccess: () =>
    set({
      isUploading: false,
      isDirty: false,
      lastUploadAt: new Date().toISOString(),
      lastUploadError: null
    }),
  markUploadError: (message) =>
    set({
      isUploading: false,
      lastUploadError: message
    }),
  setMaskNiftis: (entries) =>
    set((state) => {
      if (!state.session) return state
      return {
        session: {
          ...state.session,
          maskNiftis: entries.map((e) => ({
            annotationId: e.annotationId,
            nifti: new Uint8Array(e.nifti)
          }))
        },
        isDirty: true
      }
    }),
  setLabelRasterManifests: (entries) =>
    set((state) => {
      if (!state.session) return state
      return {
        session: {
          ...state.session,
          labelRasterManifests: entries.map((e) => ({
            annotationId: e.annotationId,
            manifest: new Uint8Array(e.manifest)
          }))
        },
        isDirty: true
      }
    }),
  toCbor: () => {
    const session = get().session
    if (!session) return null
    return encodeSessionCbor(session)
  },
  checkpointCount: () => get().session?.checkpoints.length ?? 0,
  reset: () =>
    set({
      session: null,
      isDirty: false,
      isUploading: false,
      lastUploadAt: null,
      lastUploadError: null
    })
}))
